import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema.generated';
import { randomId } from '@/lib/id';

const CSRF_HEADER = 'X-CSRF-Token';
const LEGACY_ACCESS_KEY = 'nom.access_token';
const LEGACY_REFRESH_KEY = 'nom.refresh_token';
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const retryRequests = new WeakMap<Request, Request>();

// API host resolution:
// - Dev: `VITE_API_BASE` no seteado → '' → fetch('/api/...') va al Vite dev
//   server, que proxea /api/* al backend en Docker.
// - Prod: `VITE_API_BASE` viene como build-arg desde Railway con la URL pública
//   del backend (`https://backend-xxx.up.railway.app/api`). Le quitamos el
//   `/api` final para no duplicar el prefix en las paths del OpenAPI.
const API_HOST = ((import.meta.env['VITE_API_BASE'] as string | undefined) ?? '').replace(
  /\/api\/?$/,
  '',
);

export const apiUrl = (path: string): string => `${API_HOST}${path}`;

/**
 * Auth ahora vive **solo en cookies httpOnly** que el backend setea en
 * login/refresh. Ya no leemos ni escribimos JWTs en localStorage.
 *
 * Para mutaciones (POST/PUT/PATCH/DELETE) usamos el patrón double-submit:
 * el FE envía `X-CSRF-Token` y el navegador adjunta la cookie `nompro_csrf`
 * automáticamente. El BE valida que coincidan (`app/utils/csrf.py`).
 *
 * En despliegues cross-domain (cdp.onmi.es ↔ backend-*.up.railway.app) la
 * cookie no es legible vía `document.cookie` (same-origin policy bloquea
 * cookies cross-domain). El BE expone el token también como response header
 * `X-CSRF-Token`; lo capturamos en cada response y lo guardamos en memoria.
 *
 * Para deploys same-origin (dev local con Vite proxy) seguimos leyendo del
 * cookie como fallback — funciona en ambos modos sin condicionales por env.
 */
let csrfTokenInMemory: string | null = null;

function readCsrfFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)nompro_csrf=([^;]+)/);
  return match && match[1] ? decodeURIComponent(match[1]) : null;
}

function captureCsrfFromResponse(response: Response): void {
  const headerToken = response.headers.get(CSRF_HEADER);
  if (headerToken) csrfTokenInMemory = headerToken;
}

export function getCsrfToken(): string | null {
  return csrfTokenInMemory ?? readCsrfFromCookie();
}

/**
 * Limpia cualquier residuo legacy de localStorage que pueda haber quedado en
 * sesiones previas a la migración a cookie-only. Idempotente y silencioso.
 */
export function clearLegacyAuthStorage(): void {
  try {
    localStorage.removeItem(LEGACY_ACCESS_KEY);
    localStorage.removeItem(LEGACY_REFRESH_KEY);
  } catch {
    // private mode / quota — sin problema, no había nada que limpiar.
  }
}

let refreshInFlight: Promise<boolean> | null = null;
let sessionExpiredFlag = false;
let onSessionExpired: (() => void) | null = null;

export function registerSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler;
}

async function tryRefresh(): Promise<boolean> {
  if (sessionExpiredFlag) return false;
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const res = await fetch(apiUrl('/api/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) {
        clearLegacyAuthStorage();
        sessionExpiredFlag = true;
        onSessionExpired?.();
        return false;
      }
      captureCsrfFromResponse(res);
      sessionExpiredFlag = false;
      return true;
    } catch {
      clearLegacyAuthStorage();
      sessionExpiredFlag = true;
      onSessionExpired?.();
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export function resetSessionExpired(): void {
  sessionExpiredFlag = false;
}

function prepareRequest(request: Request): Request {
  const headers = new Headers(request.headers);
  // Cookie-only auth: el navegador envía `nompro_access` automáticamente
  // gracias a `credentials: 'include'`. No tocamos Authorization.
  headers.delete('Authorization');

  if (MUTATING_METHODS.has(request.method.toUpperCase())) {
    const csrf = getCsrfToken();
    if (csrf) headers.set(CSRF_HEADER, csrf);
  }

  if (!headers.has('X-Request-Id')) {
    headers.set('X-Request-Id', randomId());
  }
  return new Request(request, { headers });
}

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const prepared = prepareRequest(request);
    retryRequests.set(prepared, prepared.clone());
    return prepared;
  },
  async onResponse({ response, request }) {
    // Captura del CSRF token desde el header de la respuesta — el BE lo
    // emite en login/refresh y en cualquier GET autenticado donde la cookie
    // falte (auto-issue). Cross-domain no podemos leer la cookie vía JS.
    captureCsrfFromResponse(response);

    const retrySource = retryRequests.get(request) ?? null;
    retryRequests.delete(request);
    if (response.status === 401 && !request.url.includes('/auth/refresh') && retrySource) {
      const ok = await tryRefresh();
      if (ok) {
        const retry = await fetch(prepareRequest(retrySource));
        captureCsrfFromResponse(retry);
        return retry;
      }
    }
    return response;
  },
};

export const api = createClient<paths>({
  // OpenAPI paths already include the `/api` prefix. baseUrl resuelve a:
  //   - dev: '' (Vite proxy)
  //   - prod: host del backend sin `/api` final, derivado de VITE_API_BASE
  baseUrl: API_HOST,
  credentials: 'include',
});

api.use(authMiddleware);

export type Paths = paths;
